'use client';
// FirstAgentPay — the "first agent" x402 payment panel (v2.15.0).
//
// Lets ANYONE (founder first) become the first real paying agent: it pays
// REAL USDC on Base mainnet from the user's OWN browser wallet (MetaMask /
// Coinbase Wallet) through the Coinbase CDP facilitator. This is a complete
// browser-side x402 client:
//   1. POST /api/x402 without payment      → 402 + `exact`/base requirements
//   2. EIP-3009 transferWithAuthorization   → signed INSIDE the wallet via
//      eth_signTypedData_v4 (EIP-712, USDC domain v2) — keys never leave the
//      wallet, gas is paid by Coinbase's facilitator, NOT by the payer.
//   3. X-PAYMENT header (base64 payload)    → node verifies via CDP, executes
//      the service, settles on-chain; the first real payment lists the
//      service in the x402 Bazaar (x402scan.com / 402index.io).
//
// Money-safety (founder red line): the platform NEVER touches private keys,
// and every micro-USDC lands in OUR OWN dedicated settlement vault
// (0x2152…5629, deterministic from the bridge seed, included in the vault
// recovery kit) — nothing is minted, nothing is planted, everything is
// recoverable and provable on Basescan.
import { useCallback, useEffect, useState } from 'react';
import {
  BadgeCheck, ExternalLink, Loader2, ShieldCheck, Sparkles, Wallet, Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { CopyBtn, fetchJson, fmtAmt, Mono } from './shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// ---------- browser wallet (EIP-1193) typings ----------
type Eip1193 = {
  request: (args: { method: string; params?: unknown[] | Record<string, unknown> }) => Promise<unknown>;
};
declare global {
  interface Window { ethereum?: Eip1193 }
}

const USDC_BASE = '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913';
const BASE_CHAIN_ID_HEX = '0x2105';
const BASESCAN = 'https://basescan.org';

// Raw-string request samples per node service (matches serviceRequestFormat)
const SAMPLES: Record<string, string> = {
  'aman-address-validator': 'AMc61c9c7a2de08bb9fa8e62c3e5640cfd3dfc3bd6e26c03ee',
  'aman-price-feed': 'BTC',
  'aman-token-audit': 'USDT',
  'aman-market-summary': '24h',
  'aman-crypto-prices': 'BTC,ETH',
  'aman-wallet-portfolio': 'AMc61c9c7a2de08bb9fa8e62c3e5640cfd3dfc3bd6e26c03ee',
  'aman-swap-quote': '100 AMAN->USDT',
  'aman-whale-watch': '5000',
  'aman-staking-rewards': '1000',
  'aman-token-launch-check': 'AMAN|AmanCoin|8|100000000',
  'aman-service-search': 'price',
  'aman-tx-lookup': '',
  'aman-block-inspect': '',
};

interface Catalog {
  baseUsdc: { payTo: string; authVerified: boolean | null; authDetail: string | null; note: string } | null;
  services: Array<{
    serviceId: string; name: string; priceAman: number;
    priceUsd: number | null; nodeOperated: boolean;
  }>;
}

type Phase = 'idle' | 'quote' | 'sign' | 'settle' | 'done';

interface SettleReceipt {
  paid: boolean;
  receipt?: { transaction: string | null; payer: string | null; amountUsd: number | null; serviceId: string };
  result?: unknown;
  note?: string;
  error?: string;
  /** honest human explanation from the node (e.g. wallet lacks native USDC on Base) */
  message?: string | null;
  /** machine-readable next-step code: insufficient-usdc-base / stale-quote / … */
  hint?: string | null;
  invalidReason?: string | null;
  invalidMessage?: string | null;
  detail?: string;
  /** Bazaar indexing outcome reported by the facilitator for THIS settlement:
   *  'success' = the resource is now cataloged and searchable by agents. */
  bazaar?: string | null;
}

const microsToUsd = (micros: string): string =>
  (Number(BigInt(micros)) / 1e6).toFixed(6);

export function FirstAgentPay({ net }: { net: 'mainnet' | 'testnet' }) {
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [account, setAccount] = useState<string | null>(null);
  const [usdc, setUsdc] = useState<number | null>(null);
  const [serviceId, setServiceId] = useState<string>('aman-address-validator');
  const [request, setRequest] = useState<string>(SAMPLES['aman-address-validator']);
  const [phase, setPhase] = useState<Phase>('idle');
  const [done, setDone] = useState<SettleReceipt | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [techLine, setTechLine] = useState<string | null>(null);
  const [fundingNeeded, setFundingNeeded] = useState(false);

  // The x402 catalog — live USD prices + CDP status from the node itself
  useEffect(() => {
    fetchJson<Catalog>(`/api/x402?net=${net}`).then(c => {
      setCatalog(c);
      const nodes = (c.services ?? []).filter(s => s.nodeOperated);
      if (nodes.length && !nodes.some(s => s.serviceId === serviceId)) {
        setServiceId(nodes[0].serviceId);
      }
    }).catch(() => { /* panel still usable via service list fallback */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [net]);

  const nodeServices = (catalog?.services ?? []).filter(s => s.nodeOperated && s.priceUsd !== null);
  const selected = nodeServices.find(s => s.serviceId === serviceId) ?? null;
  const payTo = catalog?.baseUsdc?.payTo ?? null;

  // Pre-flight funding gate — the payer wallet must hold NATIVE USDC on Base.
  // (v2.15.1: CDP simulates the real on-chain transfer during verify; an
  // unfunded wallet used to surface as a cryptic "invalid_payload".)
  const priceShortfall = selected?.priceUsd != null && usdc !== null && usdc < selected.priceUsd;
  const needFunding = priceShortfall || fundingNeeded;

  const loadUsdc = useCallback(async (eth: Eip1193, addr: string) => {
    try {
      const data = '0x70a08231' + addr.toLowerCase().replace(/^0x/, '').padStart(64, '0');
      const res = await eth.request({ method: 'eth_call', params: [{ to: USDC_BASE, data }, 'latest'] }) as string;
      setUsdc(Number(BigInt(res)) / 1e6);
    } catch { setUsdc(null); }
  }, []);

  const ensureBase = useCallback(async (eth: Eip1193) => {
    const chainId = await eth.request({ method: 'eth_chainId' }) as string;
    if (chainId === BASE_CHAIN_ID_HEX) return;
    try {
      await eth.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: BASE_CHAIN_ID_HEX }] });
    } catch (e) {
      const code = (e as { code?: number }).code;
      if (code === 4902 || code === -32603) {
        await eth.request({
          method: 'wallet_addEthereumChain',
          params: [{
            chainId: BASE_CHAIN_ID_HEX,
            chainName: 'Base',
            nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
            rpcUrls: ['https://mainnet.base.org'],
            blockExplorerUrls: [BASESCAN],
          }],
        });
      } else { throw e; }
    }
  }, []);

  const connect = useCallback(async () => {
    const eth = window.ethereum;
    if (!eth) {
      toast.error('No browser wallet found — install MetaMask or Coinbase Wallet, then refresh.');
      return;
    }
    try {
      const accounts = await eth.request({ method: 'eth_requestAccounts' }) as string[];
      const addr = accounts?.[0];
      if (!addr) return;
      setAccount(addr);
      await ensureBase(eth);
      await loadUsdc(eth, addr);
      toast.success('Wallet connected on Base — you can pay as the first agent now.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Wallet connection cancelled');
    }
  }, [ensureBase, loadUsdc]);

  // re-check the live USDC balance on demand (after the user funds the wallet)
  const refreshBalance = useCallback(async () => {
    const eth = window.ethereum;
    if (!eth || !account) return;
    await loadUsdc(eth, account);
    toast.success('USDC balance refreshed from Base');
  }, [account, loadUsdc]);

  const pay = useCallback(async () => {
    if (!account || !selected) return;
    setError(null);
    setDone(null);
    setTechLine(null);
    setFundingNeeded(false);
    const eth = window.ethereum;
    if (!eth) { toast.error('No browser wallet found'); return; }
    try {
      // ── 0. pre-flight: native USDC on Base must cover the price BEFORE we
      // ask for a signature — an unfunded signature can only be rejected by
      // Coinbase's on-chain simulation, so gate it here with clear guidance.
      if (selected.priceUsd != null && usdc !== null && usdc < selected.priceUsd) {
        setFundingNeeded(true);
        throw new Error(`Your wallet holds ${usdc.toLocaleString('en-US', { maximumFractionDigits: 6 })} USDC on Base but this service costs about $${selected.priceUsd.toFixed(4)}. Get native USDC on the Base network first — nothing was charged.`);
      }

      // ── 1. the 402 quote — exact/base requirements enforced by the node.
      // v2.16.0: we quote against the PER-SERVICE resource route
      // (POST /api/x402/<serviceId>) — the exact absolute URL the Bazaar
      // catalogs — and echo `resource` + `extensions` back inside the
      // payment payload. Per the official discovery spec, the facilitator
      // catalogs a resource only when the paying client carries the server's
      // bazaar extension AND `paymentPayload.resource` through settle.
      setPhase('quote');
      const quoteRes = await fetch(`/api/x402/${selected.serviceId}?net=${net}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request }),
      });
      const quote = await quoteRes.json() as {
        accepts?: Array<Record<string, unknown>>;
        resource?: string;
        extensions?: Record<string, unknown>;
        error?: string;
      };
      const reqs = (quote.accepts ?? []).find(a => a.scheme === 'exact' && a.network === 'base') as
        | { maxAmountRequired: string; payTo: string; asset: string; extra?: { name: string; version: string } }
        | undefined;
      if (!reqs) {
        throw new Error(quote.error ?? 'No live USD market for this service — pick another one');
      }

      // ── 2. EIP-3009 transferWithAuthorization — signed INSIDE the wallet
      setPhase('sign');
      const now = Math.floor(Date.now() / 1000);
      const nonce = '0x' + Array.from(crypto.getRandomValues(new Uint8Array(32)))
        .map(b => b.toString(16).padStart(2, '0')).join('');
      const typedData = {
        types: {
          EIP712Domain: [
            { name: 'name', type: 'string' },
            { name: 'version', type: 'string' },
            { name: 'chainId', type: 'uint256' },
            { name: 'verifyingContract', type: 'address' },
          ],
          TransferWithAuthorization: [
            { name: 'from', type: 'address' },
            { name: 'to', type: 'address' },
            { name: 'value', type: 'uint256' },
            { name: 'validAfter', type: 'uint256' },
            { name: 'validBefore', type: 'uint256' },
            { name: 'nonce', type: 'bytes32' },
          ],
        },
        primaryType: 'TransferWithAuthorization',
        domain: {
          // MUST be the REAL EIP-712 domain of native USDC on Base — proven
          // on-chain: with 'USDC' the contract reverts "invalid signature".
          name: reqs.extra?.name ?? 'USD Coin',
          version: reqs.extra?.version ?? '2',
          chainId: 8453,
          verifyingContract: reqs.asset,
        },
        message: {
          from: account,
          to: reqs.payTo,
          value: reqs.maxAmountRequired,
          validAfter: String(now - 60),
          validBefore: String(now + 900),
          nonce,
        },
      };
      await ensureBase(eth);
      const signature = await eth.request({
        method: 'eth_signTypedData_v4',
        params: [account, JSON.stringify(typedData)],
      }) as string;

      // ── 3. X-PAYMENT header → node verifies via Coinbase → executes → settles
      // The payload carries `resource` + the echoed server `extensions` —
      // this is what turns a settled payment into a Bazaar listing.
      setPhase('settle');
      const payment = {
        x402Version: 1,
        scheme: 'exact',
        network: 'base',
        resource: quote.resource ?? null,
        ...(quote.extensions ? { extensions: quote.extensions } : {}),
        payload: { signature, authorization: typedData.message },
      };
      const settleRes = await fetch(`/api/x402/${selected.serviceId}?net=${net}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-PAYMENT': btoa(unescape(encodeURIComponent(JSON.stringify(payment)))) },
        body: JSON.stringify({ request }),
      });
      const out = await settleRes.json() as SettleReceipt;
      if (out?.paid) {
        setDone(out);
        setPhase('done');
        await loadUsdc(eth, account);
        toast.success(out.bazaar === 'success'
          ? 'x402 payment settled in real USDC on Base — the facilitator just cataloged this service in the Bazaar!'
          : out.bazaar === 'processing'
            ? 'x402 payment settled in real USDC on Base — Bazaar cataloging is processing asynchronously.'
            : out.bazaar === 'rejected'
              ? 'x402 payment settled, but the facilitator rejected the discovery metadata — see the receipt below.'
              : 'x402 payment settled in real USDC on Base — the service is now Bazaar-eligible!');
      } else {
        // Nothing was charged — verify/settle rejected the payment BEFORE any
        // USDC moved. Surface the node's honest message + a technical line.
        if (out?.hint === 'insufficient-usdc-base') {
          setFundingNeeded(true);
        }
        setTechLine([out?.invalidReason, out?.invalidMessage, out?.detail && `CDP: ${out.detail}`].filter(Boolean).join(' — ') || null);
        const reason = out?.message || [out?.error, out?.invalidReason].filter(Boolean).join(' — ');
        throw new Error(reason || 'Payment was not settled — you were NOT charged');
      }
      // keep the balance display honest after every attempt
      await loadUsdc(eth, account);
    } catch (e) {
      const err = e as { code?: number; message?: string };
      const msg = err?.code === 4001 ? 'Payment cancelled in your wallet — nothing was charged'
        : (err?.message ?? 'Payment failed');
      setError(msg);
      setPhase('idle');
      toast.error(msg);
    }
  }, [account, selected, request, net, usdc, ensureBase, loadUsdc]);

  const busy = phase === 'quote' || phase === 'sign' || phase === 'settle';
  const payLabel = phase === 'quote' ? 'Requesting the x402 quote…'
    : phase === 'sign' ? 'Sign the payment in your wallet…'
    : phase === 'settle' ? 'Verifying via Coinbase & settling…'
    : `Pay ${selected?.priceUsd !== null && selected?.priceUsd !== undefined ? `$${microsToUsd(String(Math.round(selected.priceUsd * 1e6)))}` : ''} in USDC & execute`;

  const resultJson = done?.result !== null && done?.result !== undefined
    ? JSON.stringify(done.result, null, 2) : null;

  return (
    <div className="glass p-4 space-y-4 ring-1 ring-primary/25">
      {/* header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <div className="rounded-lg bg-primary/10 text-primary ring-1 ring-primary/25 p-2"><Sparkles className="size-4" /></div>
          <div>
            <div className="text-sm font-black">Be the FIRST paying agent — real USDC on Base</div>
            <div className="text-[0.68rem] text-muted-foreground">
              Your payment proves the whole x402 rail and lists our services on the public Bazaar (x402scan.com · 402index.io).
            </div>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[0.62rem] font-bold ring-1 ${catalog?.baseUsdc?.authVerified ? 'bg-emerald-500/10 text-emerald-400 ring-emerald-500/30' : 'bg-amber-500/10 text-amber-400 ring-amber-500/30'}`}>
          <ShieldCheck className="size-3" />
          Coinbase CDP {catalog?.baseUsdc?.authVerified ? 'verified' : 'connecting…'}
        </span>
      </div>

      {/* how it works strip */}
      <div className="grid md:grid-cols-3 gap-2 text-[0.66rem]">
        {[
          { t: '1 · Connect your wallet', d: 'MetaMask / Coinbase Wallet on Base — keys never leave your wallet.' },
          { t: '2 · Sign inside your wallet', d: 'EIP-3009 gasless authorization — Coinbase pays the gas, you pay the exact price only.' },
          { t: '3 · Settled on-chain', d: 'USDC lands in OUR vault, the node executes the service and returns the real result.' },
        ].map(s => (
          <div key={s.t} className="rounded-xl bg-muted/40 ring-1 ring-border/60 p-2.5">
            <div className="font-black text-[0.68rem]">{s.t}</div>
            <div className="text-muted-foreground mt-0.5 leading-relaxed">{s.d}</div>
          </div>
        ))}
      </div>

      {/* body */}
      <div className="grid md:grid-cols-2 gap-3">
        {/* wallet box */}
        <div className="rounded-xl bg-muted/40 ring-1 ring-border/60 p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs font-bold flex items-center gap-1.5"><Wallet className="size-3.5 text-primary" /> Your wallet (Base mainnet)</div>
            {!account && (
              <Button onClick={connect} size="sm" className="h-7 gap-1.5 text-[0.68rem] font-black">
                <Wallet className="size-3.5" /> Connect wallet
              </Button>
            )}
          </div>
          {account ? (
            <div className="space-y-1.5 text-[0.7rem]">
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Address</span>
                <span className="flex items-center gap-1"><Mono dir="ltr">{account.slice(0, 10)}…{account.slice(-6)}</Mono><CopyBtn text={account} /></span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">USDC balance</span>
                <span className="flex items-center gap-1.5">
                  <b dir="ltr" className={usdc !== null && usdc < (selected?.priceUsd ?? 0) ? 'text-rose-400' : 'text-emerald-400'}>
                    {usdc === null ? '…' : usdc.toLocaleString('en-US', { maximumFractionDigits: 2 })} USDC
                  </b>
                  <button onClick={refreshBalance} title="Re-check balance on Base"
                    className="rounded-md ring-1 ring-border px-1.5 py-0.5 text-[0.6rem] font-bold text-muted-foreground hover:text-primary hover:ring-primary/40 transition-colors">
                    ↻ re-check
                  </button>
                </span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Settlement vault (ours)</span>
                <span className="flex items-center gap-1">
                  <Mono dir="ltr">{payTo ? `${payTo.slice(0, 8)}…${payTo.slice(-6)}` : '…'}</Mono>
                  {payTo && <CopyBtn text={payTo} />}
                </span>
              </div>
              {usdc !== null && usdc < (selected?.priceUsd ?? 0) && (
                <div className="rounded-lg bg-amber-500/10 ring-1 ring-amber-500/30 px-2.5 py-1.5 text-[0.64rem] text-amber-300">
                  Not enough USDC on Base — get USDC (Base network) from Coinbase or bridge.base.org, then refresh.
                </div>
              )}
            </div>
          ) : (
            <div className="text-[0.68rem] text-muted-foreground">
              Connect MetaMask or Coinbase Wallet to pay in real USDC. The signature is gasless (EIP-3009) and stays inside your wallet — the platform never sees your keys.
            </div>
          )}
        </div>

        {/* service box */}
        <div className="rounded-xl bg-muted/40 ring-1 ring-border/60 p-3 space-y-2">
          <div className="text-xs font-bold flex items-center gap-1.5"><Zap className="size-3.5 text-amber-400" /> Pick a node service</div>
          <select
            value={serviceId}
            onChange={e => {
              setServiceId(e.target.value);
              setRequest(SAMPLES[e.target.value] ?? '');
            }}
            className="w-full rounded-lg bg-background ring-1 ring-border px-2.5 py-2 text-xs font-bold outline-none focus:ring-primary/50"
          >
            {nodeServices.map(s => (
              <option key={s.serviceId} value={s.serviceId}>
                {s.name} — {s.priceUsd !== null ? `$${s.priceUsd.toFixed(4)}` : `${fmtAmt(s.priceAman)} AMAN`}
              </option>
            ))}
          </select>
          <Input
            value={request}
            onChange={e => setRequest(e.target.value.slice(0, 200))}
            placeholder="Request payload (optional)"
            className="h-8 text-xs"
          />
          <Button onClick={pay} disabled={!account || busy || !selected || priceShortfall} className="w-full gap-2 font-black h-9 text-xs">
            {busy ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
            {payLabel}
          </Button>
          {!account && <div className="text-[0.62rem] text-muted-foreground text-center">Connect your wallet first — payment is signed inside it.</div>}
        </div>
      </div>

      {/* funding guidance — honest next steps when the wallet lacks native USDC on Base */}
      {needFunding && account && (
        <div className="rounded-xl bg-amber-500/10 ring-1 ring-amber-500/30 px-3 py-2.5 space-y-1 text-[0.68rem] text-amber-200">
          <div className="font-black text-amber-300">Fund your wallet with NATIVE USDC on Base — nothing was charged</div>
          <div className="leading-relaxed">
            Needed: about <b>{selected?.priceUsd != null ? `$${selected.priceUsd.toFixed(4)}` : 'the service price'}</b> — available:{' '}
            <b dir="ltr">{usdc === null ? 'unknown' : `${usdc.toLocaleString('en-US', { maximumFractionDigits: 6 })} USDC`}</b>. Get USDC on the
            <b> Base network</b> only (Coinbase → Withdraw USDC → Base, or bridge.base.org), then press “↻ re-check” and Pay.
            USDC on Ethereum/Polygon or bridged USDbC will not work for this rail.
          </div>
        </div>
      )}

      {/* error */}
      {error && (
        <div className="space-y-1">
          <div className="rounded-lg bg-rose-500/10 ring-1 ring-rose-500/30 px-3 py-2 text-xs text-rose-400">{error}</div>
          {techLine && (
            <div className="rounded-lg bg-zinc-950 ring-1 ring-border px-3 py-1.5 overflow-x-auto">
              <pre className="text-[0.6rem] text-zinc-400 whitespace-pre-wrap" dir="ltr">{techLine}</pre>
            </div>
          )}
        </div>
      )}

      {/* settled receipt */}
      {phase === 'done' && done && (
        <div className="space-y-2">
          <div className="rounded-lg bg-emerald-500/10 ring-1 ring-emerald-500/30 p-3 flex flex-wrap items-center gap-2 text-sm text-emerald-400 font-bold">
            <BadgeCheck className="size-4 shrink-0" /> Settled in real USDC on Base
            {done.receipt?.transaction && (
              <a href={`${BASESCAN}/tx/${done.receipt.transaction}`} target="_blank" rel="noreferrer"
                className="ml-auto inline-flex items-center gap-1 text-[0.68rem] underline underline-offset-2">
                Basescan <ExternalLink className="size-3" />
              </a>
            )}
          </div>
          {done.bazaar && (
            <div className={`rounded-lg px-3 py-2 text-[0.68rem] ring-1 ${done.bazaar === 'success' ? 'bg-emerald-500/10 ring-emerald-500/30 text-emerald-300' : done.bazaar === 'processing' ? 'bg-sky-500/10 ring-sky-500/30 text-sky-300' : 'bg-amber-500/10 ring-amber-500/30 text-amber-300'}`}>
              <b>Bazaar indexing: {done.bazaar}</b>{done.bazaar === 'success'
                ? ' — this resource is now cataloged by the Coinbase facilitator and searchable by agents (Bazaar MCP · Amazon Bedrock AgentCore · agentic.market).'
                : done.bazaar === 'processing'
                  ? ' — the metadata was accepted and is being cataloged asynchronously.'
                  : ' — the discovery metadata was refused for this settlement; the payment itself is fine and nothing is lost.'}
            </div>
          )}
          {resultJson && (
            <div className="rounded-xl bg-zinc-950 ring-1 ring-border p-3 overflow-x-auto">
              <pre className="text-[0.66rem] text-emerald-300/90 leading-relaxed" dir="ltr">{resultJson}</pre>
            </div>
          )}
          {done.note && <div className="text-[0.65rem] text-muted-foreground">{done.note}</div>}
        </div>
      )}

      {/* money-safety footer */}
      <div className="flex items-start gap-2 rounded-xl bg-emerald-500/5 ring-1 ring-emerald-500/20 px-3 py-2 text-[0.64rem] text-emerald-300/90">
        <ShieldCheck className="size-3.5 mt-0.5 shrink-0 text-emerald-400" />
        <span>
          Money-sovereignty: the platform never touches private keys — you sign in YOUR wallet, and every micro-USDC
          lands in our own dedicated settlement vault <Mono dir="ltr">{payTo ? `${payTo.slice(0, 10)}…` : ''}</Mono>
          (recoverable via the vault recovery kit, provable on Basescan). Nothing is minted or planted; a failed
          settlement means <b>nothing was charged</b>.
        </span>
      </div>
    </div>
  );
}
